import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleAuthProvider, EmailAuthProvider, getAuth } from 'firebase/auth'
import { usePlaidLink } from 'react-plaid-link'
import * as firebaseui from 'firebaseui'

import FirebaseAuthWidget from '../../components/firebase-auth-widget/FirebaseAuthWidget'
import Button from '../../components/button/Button'
import StepUpDialog from '../../components/step-up-dialog/StepUpDialog'
import { useAuth } from '../../_hooks/use-auth'
import {
  claimFlowFormVerification,
  grantFlowFormVerification,
} from '../../_data/flow-form-verification'
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
} from '../../_data/verifications'
import { VerificationConnectDoneMessage } from '../../_types/flow-form-verification'
import {
  isRequestComplete,
  readAction,
  readHashParameters,
  readRequest,
} from './helpers'
import { ShareOutcome, VerificationConnectState } from './types'
import {
  ContentCard,
  ErrorBanner,
  Heading,
  PageContainer,
  Subheading,
  WidgetContainer,
  Wordmark,
} from './styled'

/**
 * First-party popup that lets a renter connect their QuickPass to a form they
 * are filling in on somebody else's site.
 *
 * It does one of three jobs, named on the URL. `share` hands an existing
 * verification to the form's step, so a renter who already proved themselves is
 * not put through it again. `save` keeps a check they just passed on that form,
 * so the next one can reuse it. `connect` walks them through connecting a bank
 * and then shares what that proved.
 *
 * Connecting is only ever done here, never on the form. It is the highest
 * consequence thing a renter can be asked to do, and it is gated behind a
 * signed-in account and a fresh one-time code, which the server enforces
 * independently. A public form has neither, so rather than build a weaker
 * second way into somebody's bank, the form sends them to this window.
 *
 * The work happens HERE, signed in as the renter, rather than by handing the
 * form a credential. Nothing about who they are crosses origins: the message
 * posted back says only that they finished, and the form learns the outcome by
 * re-reading its own status from the server. That is why the message can be
 * posted without pinning it to an allowlisted origin, and it is the reason this
 * page exists instead of a token being passed around.
 *
 * @returns {JSX.Element}
 */
const VerificationConnectPage = (): JSX.Element => {
  const { quickPassUser, isLoading } = useAuth()
  const firebaseAuth = useMemo(() => getAuth(), [])
  const hasActedReference = useRef<boolean>(false)

  const parameters = useMemo(
    () => readHashParameters(window.location.hash),
    [],
  )
  const action = useMemo(() => readAction(parameters), [parameters])
  const request = useMemo(() => readRequest(parameters), [parameters])

  const [state, setState] = useState<VerificationConnectState>({
    status: 'initialising',
    action,
    errorMessage: '',
    remainingDays: 0,
  })
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [shouldOpenPlaid, setShouldOpenPlaid] = useState<boolean>(false)
  const [isStepUpOpen, setIsStepUpOpen] = useState<boolean>(false)
  const [isBusy, setIsBusy] = useState<boolean>(false)

  const postDone = useCallback(
    (wasSuccessful: boolean): void => {
      if (window.opener === null || window.opener === window) {
        return
      }

      const message: VerificationConnectDoneMessage = {
        source: 'quickpass-verification-connect',
        type: 'done',
        action,
        wasSuccessful,
      }

      // Posted without a target origin because it carries nothing worth
      // protecting: no token, no identity, no result. It is a nudge telling the
      // form to re-read its own status, which the server is the authority on.
      window.opener.postMessage(message, '*')
    },
    [action],
  )

  /**
   * Hands whatever the renter now holds to the form's step.
   *
   * Shared by the plain share action and the tail of a bank connection, because
   * the two end the same way: the check exists on this account and the form is
   * told about it.
   *
   * Having nothing to give and being refused are reported apart. The first is an
   * ordinary answer the caller may build on; the second has already put its own
   * reason on the screen, and the caller must leave it there.
   *
   * @param {string} quickPassUserId - the signed-in renter
   * @returns {Promise<ShareOutcome>} what came of it
   */
  const share = useCallback(
    async (quickPassUserId: string): Promise<ShareOutcome> => {
      const { result, reason } = await grantFlowFormVerification(
        quickPassUserId,
        request,
      )

      if (result === null) {
        setState(current => ({
          ...current,
          status: 'error',
          errorMessage: reason,
        }))
        postDone(false)
        return 'failed'
      }

      if (result.wasShared === false) {
        return 'nothing'
      }

      setState(current => ({
        ...current,
        status: 'shared',
        remainingDays: result.remainingDays,
      }))
      postDone(true)
      return 'shared'
    },
    [request, postDone],
  )

  const handlePlaidSuccess = useCallback(
    async (publicToken: string): Promise<void> => {
      if (quickPassUser === null) {
        return
      }

      setIsBusy(true)
      setLinkToken(null)

      const exchanged = await exchangePlaidPublicToken(
        quickPassUser.id,
        publicToken,
      )

      if (exchanged === null) {
        setIsBusy(false)
        setState(current => ({
          ...current,
          status: 'error',
          errorMessage:
            'We could not finish connecting your bank. Please try again.',
        }))
        postDone(false)
        return
      }

      const outcome = await share(quickPassUser.id)
      setIsBusy(false)

      // The bank connected but the check it was meant to prove is not ready.
      // Income can sit processing for a while after a fresh connection, so this
      // is a wait rather than a failure, and saying so is the honest answer. A
      // refusal is left alone, because it has already said something truer.
      if (outcome === 'nothing') {
        setState(current => ({
          ...current,
          status: 'nothing-to-share',
        }))
        postDone(false)
      }
    },
    [quickPassUser, share, postDone],
  )

  const { open: openPlaid, ready: isPlaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: publicToken => {
      void handlePlaidSuccess(publicToken)
    },
    onExit: () => {
      setShouldOpenPlaid(false)
      setIsBusy(false)
    },
  })

  useEffect(() => {
    if (shouldOpenPlaid === true && isPlaidReady === true && linkToken !== null) {
      setShouldOpenPlaid(false)
      openPlaid()
    }
  }, [shouldOpenPlaid, isPlaidReady, linkToken, openPlaid])

  const handleStepUpSatisfied = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    setIsStepUpOpen(false)
    setIsBusy(true)

    const token = await createPlaidLinkToken(quickPassUser.id)

    if (token === null) {
      setIsBusy(false)
      setState(current => ({
        ...current,
        status: 'error',
        errorMessage: 'Bank connections are not available right now.',
      }))
      return
    }

    setLinkToken(token)
    setShouldOpenPlaid(true)
    setIsBusy(false)
  }, [quickPassUser])

  const act = useCallback(
    async (quickPassUserId: string): Promise<void> => {
      setState(current => ({ ...current, status: 'working', errorMessage: '' }))

      if (action === 'save') {
        const claimed = await claimFlowFormVerification(
          quickPassUserId,
          request,
        )

        if (claimed === null || claimed.wasClaimed === false) {
          setState(current => ({
            ...current,
            status: 'error',
            errorMessage:
              'We could not keep that verification. It may already be on another QuickPass.',
          }))
          postDone(false)
          return
        }

        setState(current => ({ ...current, status: 'saved' }))
        postDone(true)
        return
      }

      const outcome = await share(quickPassUserId)

      // A refusal has already put its own reason on the screen. Carrying on
      // here would replace it with a friendlier message that is not true.
      if (outcome === 'shared' || outcome === 'failed') {
        return
      }

      // Sharing found nothing. For a plain share that is the end of it, but a
      // connect was always going to end at the bank, so this is where it starts
      // rather than where it gives up.
      if (action === 'connect') {
        setState(current => ({ ...current, status: 'needs-bank' }))
        return
      }

      setState(current => ({ ...current, status: 'nothing-to-share' }))
      postDone(false)
    },
    [action, request, share, postDone],
  )

  useEffect(() => {
    if (isLoading === true || hasActedReference.current === true) {
      return
    }

    if (isRequestComplete(request) === false) {
      hasActedReference.current = true
      setState(current => ({
        ...current,
        status: 'error',
        errorMessage: 'This window was opened without enough information.',
      }))
      return
    }

    if (quickPassUser === null) {
      setState(current => ({ ...current, status: 'needs-sign-in' }))
      return
    }

    hasActedReference.current = true
    void act(quickPassUser.id)
  }, [isLoading, quickPassUser, request, act])

  const handleSignInFailure = useCallback(
    (failure: firebaseui.auth.AuthUIError): Promise<void> => {
      console.error(failure)
      setState(current => ({
        ...current,
        status: 'error',
        errorMessage: failure.message ?? 'Sign-in failed. Try again in a sec.',
      }))
      return Promise.resolve()
    },
    [],
  )

  const firebaseLoginOptions = useMemo<firebaseui.auth.Config>(
    () => ({
      signInFlow: 'popup',
      siteName: 'QuickPass',
      callbacks: {
        signInSuccessWithAuthResult: () => false,
        signInFailure: handleSignInFailure,
      },
      signInOptions: [
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          fullLabel: 'Continue with Google',
        },
        {
          provider: EmailAuthProvider.PROVIDER_ID,
          fullLabel: 'Continue with email',
        },
      ],
    }),
    [handleSignInFailure],
  )

  const resolveHeadingText = (): string => {
    if (action === 'save') {
      return 'Keep this on your QuickPass'
    }

    if (action === 'connect') {
      return 'Connect your bank'
    }

    return 'Use your QuickPass'
  }

  const resolveSignInSubheading = (): string => {
    if (action === 'save') {
      return 'Sign in and we will keep the check you just passed, so the next place you apply does not put you through it again.'
    }

    if (action === 'connect') {
      return 'Sign in first. We will check whether you have already proved this, and only ask for your bank if you have not.'
    }

    return 'Sign in and we will pass your existing verification to this form. Nothing else is shared.'
  }

  const headingText = resolveHeadingText()

  return (
    <PageContainer>
      <ContentCard>
        <Wordmark>QuickPass</Wordmark>

        {state.status === 'error' && (
          <>
            <Heading>We hit a snag</Heading>
            <ErrorBanner role="alert">{state.errorMessage}</ErrorBanner>
            <Subheading>You can close this window and carry on.</Subheading>
          </>
        )}

        {state.status === 'needs-sign-in' && (
          <>
            <Heading>{headingText}</Heading>
            <Subheading>{resolveSignInSubheading()}</Subheading>
            <WidgetContainer>
              <FirebaseAuthWidget
                firebaseAuth={firebaseAuth}
                uiConfig={firebaseLoginOptions}
              />
            </WidgetContainer>
          </>
        )}

        {(state.status === 'initialising' || state.status === 'working') && (
          <>
            <Heading>{headingText}</Heading>
            <Subheading>One moment.</Subheading>
          </>
        )}

        {state.status === 'needs-bank' && (
          <>
            <Heading>Connect your bank</Heading>
            <Subheading>
              We will confirm it is you, then open your bank in a secure window.
              We only read what is needed to prove this, and the form is told the
              result and nothing else.
            </Subheading>
            <Button
              type="button"
              variant="primary"
              isLoading={isBusy}
              onClick={() => {
                setIsStepUpOpen(true)
              }}>
              Continue
            </Button>
          </>
        )}

        {state.status === 'shared' && (
          <>
            <Heading>Shared</Heading>
            <Subheading>
              {`That form now has your verification, good for another ${state.remainingDays} days. You can close this window.`}
            </Subheading>
          </>
        )}

        {state.status === 'saved' && (
          <>
            <Heading>Saved</Heading>
            <Subheading>
              That verification is on your QuickPass now. You can close this
              window.
            </Subheading>
          </>
        )}

        {state.status === 'nothing-to-share' && (
          <>
            <Heading>Nothing to share yet</Heading>
            <Subheading>
              {action === 'connect'
                ? 'Your bank is connected, but the check it proves is still being worked out. This can take a few minutes. Come back to the form shortly and it will be there.'
                : 'You do not have a current verification of that kind, so the form will ask you to run one. It will be saved here afterwards.'}
            </Subheading>
          </>
        )}

        <StepUpDialog
          isOpen={isStepUpOpen}
          quickPassUserId={quickPassUser === null ? '' : quickPassUser.id}
          onCancel={() => {
            setIsStepUpOpen(false)
          }}
          onSatisfied={() => {
            void handleStepUpSatisfied()
          }}
        />
      </ContentCard>
    </PageContainer>
  )
}

export default VerificationConnectPage
